import { NextSeo } from "next-seo";

import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import {
  AudienceSection,
  ComparisonHero,
  FAQAccordion,
  FeatureComparisonSection,
  IntroSection,
  PricingComparison,
  QuickComparisonTable,
  SwitchingSteps,
} from "@/features/marketing-content/components";
import { getAllComparisonSlugs, getComparisonBySlug } from "@/lib/content-loader";

import type { GetStaticPaths, GetStaticProps } from "next";
import type { ComparisonPage } from "@/content/comparisons/schema";

interface ComparisonPageProps {
  page: ComparisonPage;
}

export default function ComparisonPageView({ page }: ComparisonPageProps) {
  const baseUrl = process.env.NEXT_PUBLIC_API_HOST || "https://rebolthq.com";

  return (
    <>
      <NextSeo
        title={page.seo.title}
        description={page.seo.description}
        canonical={`${baseUrl}/comparison/${page.slug}`}
        openGraph={{
          url: `${baseUrl}/comparison/${page.slug}`,
          title: page.seo.title,
          description: page.seo.description,
        }}
      />

      <NavbarLegacy />

      <main>
        <ComparisonHero
          trustBadge={page.hero.trustBadge}
          title={page.hero.title}
          titleHighlight={page.hero.titleHighlight}
          description={page.hero.description}
          competitorLogo={page.competitor.logo}
        />

        <IntroSection
          heading={page.intro.heading}
          body={page.intro.body}
          image={page.intro.image}
          crossLink={page.intro.crossLink}
        />

        <QuickComparisonTable
          heading={page.quickComparison.heading}
          competitorName={page.competitor.name}
          rows={page.quickComparison.rows}
        />

        <FeatureComparisonSection comparisons={page.featureComparisons} competitorName={page.competitor.name} />

        <PricingComparison
          heading={page.pricing.heading}
          rebolt={page.pricing.rebolt}
          competitor={page.pricing.competitor}
          winnerNote={page.pricing.winnerNote}
        />

        <AudienceSection rebolt={page.whoShouldChooseRebolt} competitor={page.whoShouldChooseCompetitor} />

        <SwitchingSteps
          heading={page.switchingSteps.heading}
          description={page.switchingSteps.description}
          steps={page.switchingSteps.steps}
          competitorName={page.competitor.name}
        />

        <FAQAccordion heading={page.faqs.heading} items={page.faqs.items} />

        <Footer />
      </main>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = () => {
  const slugs = getAllComparisonSlugs();

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<ComparisonPageProps> = async ({ params }) => {
  const slug = params?.slug as string;
  const page = await getComparisonBySlug(slug);

  if (!page) {
    return { notFound: true };
  }

  return {
    props: { page },
  };
};
